import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Building2, Users, Plus, Mail, Trash2, FileText, Shield, ChevronRight, ArrowLeft, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Organization = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [activeTab, setActiveTab] = useState('members');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchOrg();
  }, [user, navigate]);

  const fetchOrg = async () => {
    try {
      const data = await api.getMyOrg();
      setOrg(data);
      if (data) {
        const orgContracts = await api.getOrgContracts();
        setContracts(orgContracts);
      }
    } catch {
      // No org yet
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!orgName.trim()) return;
    setCreating(true);
    try {
      await api.createOrg(orgName.trim());
      toast.success('Organizacion creada exitosamente');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear organizacion');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.inviteMember(inviteEmail.trim());
      toast.success(`${inviteEmail} agregado a la organizacion`);
      setInviteEmail('');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al invitar');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId, name) => {
    if (!window.confirm(`Eliminar a ${name} de la organizacion?`)) return;
    try {
      await api.removeMember(userId);
      toast.success('Miembro eliminado');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const isOwner = org && org.owner_id === user?.id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#1A4D2E' }} />
      </div>
    );
  }

  // No org yet — show creation screen
  if (!org) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
        <nav className="bg-white border-b shadow-sm sticky top-0 z-20">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl flex items-center h-14">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="logo" className="h-8 w-auto" />
              <span className="text-lg font-semibold" style={{ color: '#1A4D2E' }}>contractcheck360</span>
            </Link>
          </div>
        </nav>
        <div className="container mx-auto px-4 max-w-lg py-16">
          <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#1A4D2E12' }}>
              <Building2 className="w-8 h-8" style={{ color: '#1A4D2E' }} />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#1A4D2E' }}>Modo B2B</h1>
            <p className="text-gray-500 text-sm mb-6">
              Crea una organizacion para que tu equipo comparta y gestione contratos juntos.
            </p>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Nombre de tu organizacion"
              className="w-full px-4 py-3 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-800"
              data-testid="org-name-input"
            />
            <Button
              onClick={handleCreate}
              disabled={creating || !orgName.trim()}
              className="w-full"
              style={{ backgroundColor: '#1A4D2E' }}
              data-testid="create-org-btn"
            >
              {creating ? 'Creando...' : 'Crear Organizacion'}
            </Button>
            <Link to="/dashboard" className="block mt-4 text-sm text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-4 h-4 inline mr-1" /> Volver al dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Has org — show org dashboard
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      <nav className="bg-white border-b shadow-sm sticky top-0 z-20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl flex items-center justify-between h-14">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="logo" className="h-8 w-auto" />
            <span className="text-lg font-semibold" style={{ color: '#1A4D2E' }}>contractcheck360</span>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Dashboard</Button>
          </Link>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-5xl py-6">
        {/* Org Header */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1A4D2E' }}>
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{ color: '#1A4D2E' }} data-testid="org-name">{org.name}</h1>
                <p className="text-sm text-gray-500">{org.members?.length || 0} miembros &middot; {contracts.length} contratos</p>
              </div>
            </div>
            {isOwner && (
              <span className="text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700">
                <Shield className="w-3 h-3 inline mr-1" /> Propietario
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 border">
          <button
            onClick={() => setActiveTab('members')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'members' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            style={activeTab === 'members' ? { backgroundColor: '#1A4D2E' } : {}}
            data-testid="tab-members"
          >
            <Users className="w-4 h-4" /> Miembros
          </button>
          <button
            onClick={() => setActiveTab('contracts')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'contracts' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            style={activeTab === 'contracts' ? { backgroundColor: '#1A4D2E' } : {}}
            data-testid="tab-contracts"
          >
            <FileText className="w-4 h-4" /> Contratos del Equipo
          </button>
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            {/* Invite */}
            {isOwner && (
              <div className="bg-white rounded-lg border shadow-sm p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Agregar miembro</h3>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-800"
                    data-testid="invite-email-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  />
                  <Button
                    onClick={handleInvite}
                    disabled={inviting || !inviteEmail.trim()}
                    size="sm"
                    style={{ backgroundColor: '#1A4D2E' }}
                    data-testid="invite-btn"
                  >
                    <Mail className="w-4 h-4 mr-1" /> {inviting ? 'Agregando...' : 'Agregar'}
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">El usuario debe estar registrado en ContractCheck360</p>
              </div>
            )}

            {/* Members list */}
            <div className="bg-white rounded-lg border shadow-sm">
              {(org.members || []).map((member, i) => (
                <div key={member.user_id} className={`flex items-center justify-between p-4 ${i > 0 ? 'border-t' : ''}`} data-testid={`member-${member.user_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium text-white" style={{ backgroundColor: member.role === 'owner' ? '#1A4D2E' : '#6B7280' }}>
                      {(member.full_name || member.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{member.full_name || member.email}</p>
                      <p className="text-xs text-gray-400">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${member.role === 'owner' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {member.role === 'owner' ? 'Propietario' : 'Miembro'}
                    </span>
                    {isOwner && member.role !== 'owner' && (
                      <button
                        onClick={() => handleRemove(member.user_id, member.full_name || member.email)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        data-testid={`remove-member-${member.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contracts Tab */}
        {activeTab === 'contracts' && (
          <div className="bg-white rounded-lg border shadow-sm">
            {contracts.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No hay contratos en la organizacion</p>
              </div>
            ) : (
              contracts.map((c, i) => (
                <div key={c.id} className={`flex items-center justify-between p-4 ${i > 0 ? 'border-t' : ''}`} data-testid={`org-contract-${c.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.file_name}</p>
                      <p className="text-xs text-gray-400">Subido por {c.uploaded_by_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {c.risk_score !== null && c.risk_score !== undefined && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        c.risk_level === 'ALTO' ? 'bg-red-100 text-red-700' :
                        c.risk_level === 'MEDIO' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {c.risk_level || 'Sin analisis'}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      c.status === 'analyzed' ? 'bg-green-50 text-green-600' :
                      c.status === 'processing' ? 'bg-blue-50 text-blue-600' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {c.status === 'analyzed' ? 'Analizado' : c.status === 'processing' ? 'Procesando' : 'Subido'}
                    </span>
                    {c.status === 'analyzed' && (
                      <Link to={`/analysis/${c.id}`}>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </Link>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Organization;
